/**
 * Staff-staged hub file releases — immutable copies under `rfq-hub-staging/{rfqProjectId}/...`
 * via Cloud Function `releaseProjectFileToRfqStaging`.
 */

import { db, storage } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import type {
  ProjectFileStreamKind,
  ProposalVersionSource,
  RfqHubFileRelease,
  RfqReleasePurpose,
} from '../types';
import { parseRfqReleasePurpose } from './rfqReleasePurpose';

const SUBCOLLECTION = 'hubFileReleases';

export type RfqStagingReleasableStreamKind = Extract<
  ProjectFileStreamKind,
  'timber_list_csv' | 'three_d_raw'
>;

export function isRfqStagingReleasableStreamKind(
  k: ProjectFileStreamKind,
): k is RfqStagingReleasableStreamKind {
  return k === 'timber_list_csv' || k === 'three_d_raw';
}

/**
 * Supplier-facing default name for a staged release. Structure-tagged timber lists
 * carry the structure ("Sales Barn timber list") so three barns staged separately
 * never read as three versions of one list; whole-project lists stay "Timber List".
 */
export function defaultRfqStagingDisplayLabel(
  kind: RfqStagingReleasableStreamKind,
  structureNames?: ReadonlyArray<string> | null,
): string {
  const names = (structureNames ?? []).map(n => (n || '').trim()).filter(Boolean);
  if (kind === 'timber_list_csv') {
    if (names.length === 0) return 'Timber List';
    const shown = names.length <= 2 ? names.join(' + ') : `${names.slice(0, 2).join(' + ')} +${names.length - 2}`;
    return `${shown} timber list`.slice(0, 60);
  }
  return '3D Model';
}

function mapReleaseDoc(d: QueryDocumentSnapshot<DocumentData>): RfqHubFileRelease {
  const data = d.data();
  return {
    id: d.id,
    rfqProjectId: String(data.rfqProjectId || ''),
    dealId: String(data.dealId || ''),
    workType: data.workType === 'cnc_quote' ? 'cnc_quote' : 'timber_rfq',
    sourceProjectId: String(data.sourceProjectId || ''),
    sourceFileId: String(data.sourceFileId || ''),
    sourceStreamId: String(data.sourceStreamId || ''),
    sourceStreamKind: (data.sourceStreamKind as ProjectFileStreamKind) || 'general',
    sourceStreamTitle: String(data.sourceStreamTitle || ''),
    structureIds: Array.isArray(data.structureIds)
      ? data.structureIds.filter((x: unknown): x is string => typeof x === 'string' && !!x)
      : undefined,
    structureNames: Array.isArray(data.structureNames)
      ? data.structureNames.map((x: unknown) => String(x ?? ''))
      : undefined,
    releasePurpose: parseRfqReleasePurpose(data.releasePurpose),
    versionIndex: typeof data.versionIndex === 'number' ? data.versionIndex : undefined,
    versionLabel: data.versionLabel ? String(data.versionLabel) : undefined,
    trueDimensions: data.trueDimensions === true ? true : undefined,
    displayLabel: String(data.displayLabel || ''),
    releaseVersionIndex: typeof data.releaseVersionIndex === 'number' ? data.releaseVersionIndex : 0,
    storagePath: String(data.storagePath || ''),
    releasedUrl: String(data.releasedUrl || ''),
    fileName: String(data.fileName || ''),
    mimeType: data.mimeType ? String(data.mimeType) : undefined,
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : undefined,
    releasedByUid: String(data.releasedByUid || ''),
    releasedByName: data.releasedByName ? String(data.releasedByName) : undefined,
    createdAt: data.createdAt?.toDate?.() ?? new Date(),
    linkedProposalVersionId: data.linkedProposalVersionId
      ? String(data.linkedProposalVersionId)
      : data.linkedProposalVersionId === null
        ? null
        : undefined,
    linkedProposalVersionLabel: data.linkedProposalVersionLabel
      ? String(data.linkedProposalVersionLabel)
      : undefined,
    linkedProposalVersionSource: data.linkedProposalVersionSource as ProposalVersionSource | undefined,
    linkedProposalVersionSavedAt: data.linkedProposalVersionSavedAt?.toDate?.() ?? undefined,
    linkedFrom:
      data.linkedFrom === 'proposal_push' || data.linkedFrom === 'hub_file_stage'
        ? data.linkedFrom
        : undefined,
  };
}

export function subscribeRfqHubFileReleases(
  rfqProjectId: string,
  onData: (releases: RfqHubFileRelease[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(db, 'rfqProjects', rfqProjectId, SUBCOLLECTION),
    orderBy('releaseVersionIndex', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map(mapReleaseDoc)),
    (err) => onError?.(err),
  );
}

/** Subscribe to staged supplier releases for a Project Hub file stream (source of truth). */
export function subscribeProjectStreamHubReleases(
  sourceProjectId: string,
  sourceStreamId: string,
  onData: (releases: RfqHubFileRelease[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collectionGroup(db, SUBCOLLECTION),
    where('sourceProjectId', '==', sourceProjectId),
    where('sourceStreamId', '==', sourceStreamId),
    orderBy('releaseVersionIndex', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map(mapReleaseDoc)),
    (err) => onError?.(err),
  );
}

/** @deprecated Prefer subscribeProjectStreamHubReleases — hub is source of truth. */
export function subscribeStreamHubReleases(
  rfqProjectId: string,
  sourceStreamId: string,
  onData: (releases: RfqHubFileRelease[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(db, 'rfqProjects', rfqProjectId, SUBCOLLECTION),
    where('sourceStreamId', '==', sourceStreamId),
    orderBy('releaseVersionIndex', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map(mapReleaseDoc)),
    (err) => onError?.(err),
  );
}

export async function getRfqHubFileRelease(
  rfqProjectId: string,
  releaseId: string,
): Promise<RfqHubFileRelease | null> {
  const snap = await getDoc(doc(db, 'rfqProjects', rfqProjectId, SUBCOLLECTION, releaseId));
  if (!snap.exists()) return null;
  return mapReleaseDoc(snap as QueryDocumentSnapshot<DocumentData>);
}

/** Staff edit of the supplier-facing staged release name (hub doc + published packages). */
export async function updateRfqHubReleaseSupplierDisplayLabel(input: {
  rfqProjectId: string;
  releaseId: string;
  displayLabel: string;
  publishedPackageIds?: string[];
}): Promise<void> {
  const rfqProjectId = input.rfqProjectId.trim();
  const releaseId = input.releaseId.trim();
  const displayLabel = input.displayLabel.trim();
  if (!rfqProjectId || !releaseId) {
    throw new Error('Release is unavailable.');
  }
  if (!displayLabel) {
    throw new Error('Enter a name for suppliers to see.');
  }

  await updateDoc(doc(db, 'rfqProjects', rfqProjectId, SUBCOLLECTION, releaseId), {
    displayLabel,
  });

  const packageIds = Array.from(new Set((input.publishedPackageIds || []).map((id) => id.trim()).filter(Boolean)));
  if (!packageIds.length) return;

  const batch = writeBatch(db);
  packageIds.forEach((packageId) => {
    batch.update(doc(db, 'rfqPackages', packageId), { releaseDisplayLabel: displayLabel });
  });
  await batch.commit();
}

/** Delete staging storage + Firestore doc for one hub release. */
async function deleteHubFileReleaseStorageAndDoc(
  releaseRef: ReturnType<typeof doc>,
  storagePath?: string,
): Promise<void> {
  const path = (storagePath || '').trim();
  if (path) {
    try {
      await deleteObject(ref(storage, path));
    } catch {
      // Staging copy may already be gone.
    }
  }
  await deleteDoc(releaseRef);
}

/** Remove one staff-staged supplier release (Storage copy + Firestore doc). */
export async function deleteRfqHubFileRelease(release: RfqHubFileRelease): Promise<void> {
  const rfqProjectId = release.rfqProjectId.trim();
  const releaseId = release.id?.trim();
  if (!rfqProjectId || !releaseId) {
    throw new Error('Release is unavailable.');
  }
  await deleteHubFileReleaseStorageAndDoc(
    doc(db, 'rfqProjects', rfqProjectId, SUBCOLLECTION, releaseId),
    release.storagePath,
  );
}

/** Remove staff-staged supplier copies when a Project Hub version file is deleted. */
export async function deleteHubFileReleasesForSourceFile(sourceFileId: string): Promise<void> {
  const fileId = sourceFileId.trim();
  if (!fileId) return;

  const snap = await getDocs(
    query(collectionGroup(db, SUBCOLLECTION), where('sourceFileId', '==', fileId)),
  );
  if (snap.empty) return;

  await Promise.all(
    snap.docs.map(async (releaseDoc) => {
      await deleteHubFileReleaseStorageAndDoc(
        releaseDoc.ref,
        String(releaseDoc.data().storagePath || ''),
      );
    }),
  );
}

export type ReleaseProjectFileToRfqStagingInput = {
  dealId: string;
  projectFileId: string;
  timberProjectId: string;
  displayLabel?: string;
  timberProjectName?: string;
  dealTitle?: string;
  /** Optional — server finds or creates the work order when omitted. */
  rfqProjectId?: string;
  linkedProposalVersionId?: string;
  /** `reference` = refined file for an already-approved quote (cut from, not requoted). Default `quote`. */
  releasePurpose?: RfqReleasePurpose;
  /**
   * When true, `structureIds` / `structureNames` replace the version track's tags
   * on this staged release. An empty list covers the whole project.
   */
  overrideStructures?: boolean;
  structureIds?: string[];
  structureNames?: string[];
};

export type ReleaseProjectFileToRfqStagingResult = {
  releaseId: string;
  rfqProjectId: string;
  releasedUrl: string;
  releasedStoragePath: string;
  name: string;
  releaseVersionIndex: number;
};

export type PushProposalTimberToRfqStagingInput = {
  dealId: string;
  timberProjectId?: string;
  timberProjectName?: string;
  dealTitle?: string;
  rfqProjectId?: string;
  displayLabel?: string;
};

export type PushProposalTimberToRfqStagingResult = {
  releaseId: string;
  rfqProjectId: string;
  proposalVersionId: string;
  releaseVersionIndex: number;
  releasedUrl: string;
  name: string;
};

export async function pushProposalTimberToRfqStaging(
  input: PushProposalTimberToRfqStagingInput,
): Promise<PushProposalTimberToRfqStagingResult> {
  const functions = getFunctions(undefined, 'us-central1');
  const callable = httpsCallable<
    PushProposalTimberToRfqStagingInput,
    PushProposalTimberToRfqStagingResult
  >(functions, 'pushProposalTimberToRfqStaging');
  const result = await callable(input);
  return result.data;
}

export async function releaseProjectFileToRfqStaging(
  input: ReleaseProjectFileToRfqStagingInput,
): Promise<ReleaseProjectFileToRfqStagingResult> {
  const functions = getFunctions(undefined, 'us-central1');
  const callable = httpsCallable<
    ReleaseProjectFileToRfqStagingInput,
    ReleaseProjectFileToRfqStagingResult
  >(functions, 'releaseProjectFileToRfqStaging');
  const result = await callable(input);
  return result.data;
}

export type PromoteHubReleaseToVendorInput = {
  rfqProjectId: string;
  hubReleaseId: string;
  supplierOrgId: string;
  dealId: string;
};

export type PromoteHubReleaseToVendorResult = {
  releaseId: string;
  releasedUrl: string;
  name: string;
  releaseVersionIndex: number;
};

export async function promoteHubReleaseToVendor(
  input: PromoteHubReleaseToVendorInput,
): Promise<PromoteHubReleaseToVendorResult> {
  const functions = getFunctions(undefined, 'us-central1');
  const callable = httpsCallable<PromoteHubReleaseToVendorInput, PromoteHubReleaseToVendorResult>(
    functions,
    'promoteHubReleaseToVendor',
  );
  const result = await callable(input);
  return result.data;
}
